import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { RootState, AppDispatch } from "src/store";
import {
  setPersonaProjects,
  setPersonasLoading,
} from "../states/personasHomeSlice";
import { fetchPersonaProjects } from "../serviceHelper";

interface Options {
  /** When true, clears existing projects before fetching so stale data is never shown. */
  freshOnly?: boolean;
}

// Module-level dedup: one in-flight fetch shared across all component instances.
// Prevents duplicate calls from Strict Mode double-mount AND from multiple
// components (ChatPage, PersonasHome, Setup) all calling usePersonaProjects().
let _inflight: Promise<void> | null = null;

/**
 * Fetches persona projects from the API on every mount and keeps Redux in sync.
 *
 * - Default: renders existing Redux data immediately, refreshes in background.
 * - freshOnly: clears stale data first so only the API response is shown.
 */
export function usePersonaProjects(opts?: Options) {
  const dispatch = useDispatch<AppDispatch>();
  const { isLoading, personaProjects } = useSelector(
    (state: RootState) => state.personasHomeSlice
  );
  const freshOnly = opts?.freshOnly ?? false;

  useEffect(() => {
    if (_inflight) return;

    if (freshOnly) {
      dispatch(setPersonaProjects([]));
    }
    dispatch(setPersonasLoading(true));

    _inflight = (async () => {
      try {
        const res = await fetchPersonaProjects();
        dispatch(setPersonaProjects(res.projects));
      } catch {
        dispatch(setPersonaProjects([]));
      } finally {
        _inflight = null;
        dispatch(setPersonasLoading(false));
      }
    })();
  }, [dispatch, freshOnly]);

  return { personaProjects, isLoading };
}
